import "./Categories.css";
import React, {memo, useCallback, useState} from "react";
import Select from "react-select";
import {AttTestOperator, DataLoadingStatus, GroupOperation, IndicatorSiblingType} from "../../../Utils/enums";
import {createIndicator, deleteIndicator, updateIndicator} from "../../../Utils/queries";
import {DivisionAttribute, DivisionFull, Indicator, IndicatorGroup} from "../../../Utils/response-types";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faEdit, faCheck, faTimes, faObjectGroup, faTrash} from "@fortawesome/free-solid-svg-icons";

const IndicatorDisplay = ({indicator, sibling_id, sibling_type, create_group, group_type, division_id, category_id, attributes_list, setDivisions, status, setStatus, onCancelCreate, onCancelCreateGroup, onSuccessfulCreate, onInitiateGroupCreation}: {indicator?: Indicator, sibling_id?: number, sibling_type?: IndicatorSiblingType, create_group?: boolean, group_type?: GroupOperation, division_id: number, category_id: number, attributes_list: DivisionAttribute[], setDivisions: React.Dispatch<React.SetStateAction<DivisionFull[]>>, status: DataLoadingStatus, setStatus: React.Dispatch<React.SetStateAction<DataLoadingStatus>>, onCancelCreate: () => void, onCancelCreateGroup: (sibling_id: number, sibling_type: IndicatorSiblingType) => void, onSuccessfulCreate: () => void, onInitiateGroupCreation: (sibling_id: number, sibling_type: IndicatorSiblingType) => void}) => {
    const [edit, setEdit] = useState<boolean>(!indicator || indicator.category_indicator_id === -1);
    const [keyword, setKeyword] = useState<string>(indicator?.compared_value ?? "");
    const [operation, setOperation] = useState<AttTestOperator>(indicator?.operator ?? AttTestOperator.In);
    const [columns, setColumns] = useState<number[]>(attributes_list.map((cc, idx) => {return {c_id: cc.division_att_id, idx: idx}}).filter(c => indicator?.compared_columns.some(cc => cc.division_att_id === c.c_id)).map(c => c.idx) ?? []);

    const stylesSufix = operation === AttTestOperator.Equals? "eq" : operation === AttTestOperator.NotEquals? "neq" : operation === AttTestOperator.In? "in" : "nin";

    const handleEditIndicator = useCallback(() => {
        setEdit(true);
    }, [setEdit]);

    const handleEditExistingConfirm = useCallback(() => {
        const recursively_process_group: (ig: IndicatorGroup) => IndicatorGroup = (ig: IndicatorGroup) => {
            return {...ig, child_groups: ig.child_groups.map(g => recursively_process_group(g)), child_indicators: ig.child_indicators.map(ind => ind.category_indicator_id !== indicator?.category_indicator_id? ind : {...ind, compared_value: keyword, operator: operation, compared_columns: columns.map(c => attributes_list[c])})};
        }

        setStatus(DataLoadingStatus.Loading);
        let old_div_list: DivisionFull[] = [];
        setDivisions((prev) => {
          old_div_list = prev;
          return prev.map((div) =>
            div.division_id === division_id
              ? { ...div, categories: div.categories.map(c => c.category_id === category_id
                    ? {...c, indicator_groups: c.indicator_groups.map(ig => recursively_process_group(ig))}
                    : c
                ) }
              : div
          );
        });
        updateIndicator(indicator?.category_indicator_id!, keyword, operation, columns.map(c => attributes_list[c].division_att_id)).then(resp => {
            if (resp.errorCode !== undefined || !resp.success) {
                alert(resp.message || `Error: ${resp.errorCode}`);
                setDivisions(old_div_list);
                setStatus(DataLoadingStatus.Loaded);
            } else {
                alert("Indicator updated successfully!")
                setStatus(DataLoadingStatus.NotLoaded);
            }
        });
    }, [keyword, operation, columns, indicator, attributes_list, division_id, category_id, setDivisions, setStatus]);

    const handleAddNewConfirm = useCallback(() => {
        const recursively_process_group: (ig: IndicatorGroup) => IndicatorGroup = (ig: IndicatorGroup) => {
            return ((sibling_type === IndicatorSiblingType.Indicator && ig.child_indicators.some(i => i.category_indicator_id === sibling_id)) || (sibling_type === IndicatorSiblingType.Group && ig.child_groups.some(ig => ig.indicator_group_id === sibling_id)))
                ? {...ig, child_indicators: [...ig.child_indicators, {category_indicator_id: -1, compared_value: keyword, operator: operation, compared_columns: columns.map(c => attributes_list[c])}]}
                : {...ig, child_groups: ig.child_groups.map(g => recursively_process_group(g))};
        }

        setStatus(DataLoadingStatus.Loading);
        let old_div_list: DivisionFull[] = [];
        setDivisions((prev) => {
          old_div_list = prev;
          return prev.map((div) =>
            div.division_id === division_id
              ? { ...div, categories: div.categories.map(c => c.category_id === category_id
                    ? {...c, indicator_groups: c.indicator_groups.map(ig => recursively_process_group(ig))}
                    : c
                ) }
              : div
          );
        });
        onSuccessfulCreate();
        createIndicator(division_id, category_id, keyword, operation, columns.map(c => attributes_list[c].division_att_id), sibling_id, sibling_type, create_group, group_type).then(resp => {
            if (resp.errorCode !== undefined || !resp.success){
                alert(resp.message || `Error: ${resp.errorCode}`);
                setDivisions(old_div_list);
                setStatus(DataLoadingStatus.Loaded);
            } else {
                alert("Indicator updated successfully!")
                setStatus(DataLoadingStatus.NotLoaded);
            }
        });

    }, [keyword, operation, columns, division_id, category_id, sibling_id, sibling_type, attributes_list, create_group, group_type, setStatus, setDivisions, onSuccessfulCreate]);

    const handleEditConfirm = useCallback(() => {
        setEdit(false);
        if (indicator !== undefined && indicator.category_indicator_id !== -1) {
            handleEditExistingConfirm();
        } else {
            handleAddNewConfirm();
        }

    }, [setEdit, indicator, handleEditExistingConfirm, handleAddNewConfirm]);

    const handleEditCancel = useCallback(() => {
        setEdit(false);
        setKeyword(indicator?.compared_value ?? "");
        setOperation(indicator?.operator ?? AttTestOperator.In);
        setColumns(attributes_list.map((cc, idx) => {return {c_id: cc.division_att_id, idx: idx}}).filter(c => indicator?.compared_columns.some(cc => cc.division_att_id === c.c_id)).map(c => c.idx) ?? []);

        console.log(attributes_list.map((cc, idx) => {return {c_id: cc.division_att_id, idx: idx}}).filter(c => indicator?.compared_columns.some(cc => cc.division_att_id === c.c_id)).map(c => c.idx) ?? []);

        if (indicator === undefined || indicator.category_indicator_id === -1) {
            if (create_group) {
                onCancelCreateGroup(sibling_id!, sibling_type!);
            } else {
                onCancelCreate();
            }
        }
    }, [indicator, attributes_list, create_group, sibling_id, sibling_type, onCancelCreate, onCancelCreateGroup, setEdit, setKeyword, setOperation, setColumns]);

    const handleCreateGroupWith = useCallback(() => {
        onInitiateGroupCreation(indicator!.category_indicator_id, IndicatorSiblingType.Indicator)
    }, [indicator, onInitiateGroupCreation]);

    const handleDeleteIndicator = useCallback(() => {
        const recursively_process_group: (ig: IndicatorGroup) => IndicatorGroup = (ig: IndicatorGroup) => {return {...ig, child_groups: ig.child_groups.map(g => recursively_process_group(g)), child_indicators: ig.child_indicators.filter(ind => ind.category_indicator_id !== indicator?.category_indicator_id)}};

        // eslint-disable-next-line no-restricted-globals
        const confirmed = confirm("Do you really want to delete this category and all its rules? This action cannot be undone!");
        if (confirmed) {
            setStatus(DataLoadingStatus.Loading);
            let old_div_list: DivisionFull[] = [];
            setDivisions((prev) => {
              old_div_list = prev;
              return prev.map((div) =>
                div.division_id === division_id
                  ? { ...div, categories: div.categories.map(c => c.category_id === category_id
                        ? {...c, indicator_groups: c.indicator_groups.map(ig => recursively_process_group(ig))}
                        : c
                        ) }
                  : div
              )
            });
            deleteIndicator(indicator?.category_indicator_id!).then(resp => {
                if (resp.errorCode !== undefined || !resp.success){
                    alert(resp.message || `Error: ${resp.errorCode}`);
                    setDivisions(old_div_list);
                } else {
                    alert("Indicator deleted successfully!")
                }
            });
        }
    }, [division_id, indicator, category_id, setDivisions, setStatus]);

    return (
        <tr className={"indicator-teeest-content"}>
            <td className="indicator-td">{edit ? <input type="text" id="newLogin" value={keyword} onChange={(event) => {setKeyword(event.target.value);}}/> : (indicator?.compared_value ?? "")}</td>
            <td className="indicator-td">{edit ? <select value={operation.valueOf()} className={`group-operation-select select-${stylesSufix}`} onClick={(e) => e.stopPropagation()} onChange={(e) => setOperation(Number(e.target.value))}>
                        <option value="1" className="select-eq">Equals</option>
                        <option value="2" className="select-neq">Not Equals</option>
                        <option value="3" className="select-in">In</option>
                        <option value="4" className="select-nin">Not In</option>
            </select> : <div className={`group-operation-selected select-${stylesSufix}`}>{AttTestOperator[indicator?.operator ?? AttTestOperator.In]}</div>}</td>
            <td className="indicator-td">{edit? <Select
                                      options={attributes_list.map(((att, idx) => {return { label: att.attribute_name, value: String(idx) };}))}
                                      isMulti
                                      placeholder="Type to search..."
                                      value={columns.map(c => attributes_list[c]).map((a, idx) => {return {label: a.attribute_name, value: String(idx)}})}
                                      onChange={(val) => setColumns(val.map(sc => Number(sc.value)))} />
            : <div className="compared-columns-wrapper">{indicator?.compared_columns.map(cc => <div className="compared-column">{cc.attribute_name}</div>)}</div>}</td>
            <td className="indicator-td indicator-actions-td">
                {status !== DataLoadingStatus.Loading && indicator?.category_indicator_id !== -1 && !edit &&
                    <button className="group-action-button" aria-label="Edit" title="Edit"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleEditIndicator()
                            }}><FontAwesomeIcon icon={faEdit}/></button>}
                {status !== DataLoadingStatus.Loading && edit && keyword && columns.length > 0 && (keyword !== indicator?.compared_value || operation !== indicator?.operator || columns.length !== indicator?.compared_columns.length || columns.some(cc1 => !indicator?.compared_columns.some(cc2 => cc2.division_att_id === attributes_list[cc1].division_att_id))) &&
                    <button className="group-action-button" aria-label="Confirm" title="Confirm"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleEditConfirm()
                            }}><FontAwesomeIcon icon={faCheck}/></button>}
                {status !== DataLoadingStatus.Loading && edit && (indicator ?? sibling_id) !== undefined &&
                    <button className="group-action-button" aria-label="Cancel" title="Cancel"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleEditCancel()
                            }}><FontAwesomeIcon icon={faTimes}/></button>}
                {status !== DataLoadingStatus.Loading && !edit &&
                    <button className="group-action-button" aria-label="Create Group With" title="Create Group With"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleCreateGroupWith()
                            }}><FontAwesomeIcon icon={faObjectGroup}/></button>}
                {status !== DataLoadingStatus.Loading && !edit &&
                    <button className="group-action-button" aria-label="Delete" title="Delete"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteIndicator()
                            }}><FontAwesomeIcon icon={faTrash}/></button>}
            </td>
        </tr>
    );
};


export default memo(IndicatorDisplay);